import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';
import Footer from './Footer';

function AccountPage({ navigateTo, goBack, isLoggedIn, currentUser, userProfile }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [solveStats, setSolveStats] = useState(null);

  // Email notification preferences state
  const [emailPreferences, setEmailPreferences] = useState({
    final_result: { enabled: false, difficulty: 'all' },
    next_puzzle: { enabled: false },
    daily_reminder: { enabled: false, send_hour_utc: 12 }
  });
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [preferencesSaveMessage, setPreferencesSaveMessage] = useState('');

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch solve statistics
  useEffect(() => {
    if (currentUser) {
      fetchSolveStats();
      fetchEmailPreferences();
    }
  }, [currentUser]);

  const fetchSolveStats = async () => {
    try {
      const { data, error } = await supabase
        .from('solves')
        .select('*')
        .eq('user_id', currentUser.id);

      if (error) {
        console.error('Error fetching solve stats:', error);
        return;
      }

      if (data && data.length > 0) {
        // Calculate stats
        const totalCompleted = data.length;
        const firstSolve = data.reduce((earliest, solve) => 
          new Date(solve.solved_at) < new Date(earliest.solved_at) ? solve : earliest
        );
        
        // Best times per difficulty
        const bestTimes = {
          easy: Math.min(...data.filter(s => s.difficulty === 'easy').map(s => s.time_seconds)),
          hard: Math.min(...data.filter(s => s.difficulty === 'hard').map(s => s.time_seconds)),
          master: Math.min(...data.filter(s => s.difficulty === 'master').map(s => s.time_seconds))
        };

        setSolveStats({
          totalCompleted,
          firstSolveDate: new Date(firstSolve.solved_at).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }),
          bestTimes
        });
      }
    } catch (error) {
      console.error('Error fetching solve stats:', error);
    }
  };

  const fetchEmailPreferences = async () => {
    setIsLoadingPreferences(true);
    try {
      const { data, error } = await supabase
        .from('email_preferences')
        .select('*')
        .eq('user_id', currentUser.id);

      if (error) {
        console.error('Error fetching email preferences:', error);
        setIsLoadingPreferences(false);
        return;
      }

      // Parse the preferences into our state structure
      const prefs = {
        final_result: { enabled: false, difficulty: 'all' },
        next_puzzle: { enabled: false },
        daily_reminder: { enabled: false, send_hour_utc: 12 }
      };

      if (data && data.length > 0) {
        data.forEach(pref => {
          if (pref.type === 'final_result') {
            prefs.final_result = {
              enabled: pref.is_enabled,
              difficulty: pref.difficulty || 'all'
            };
          } else if (pref.type === 'next_puzzle') {
            prefs.next_puzzle = {
              enabled: pref.is_enabled
            };
          } else if (pref.type === 'daily_reminder') {
            prefs.daily_reminder = {
              enabled: pref.is_enabled,
              send_hour_utc: pref.send_hour_utc || 12
            };
          }
        });
      }

      setEmailPreferences(prefs);
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  const saveEmailPreferences = async () => {
    setIsSavingPreferences(true);
    setPreferencesSaveMessage('');

    try {
      // Save final_result preference
      const { error: finalResultError } = await supabase
        .from('email_preferences')
        .upsert({
          user_id: currentUser.id,
          type: 'final_result',
          is_enabled: emailPreferences.final_result.enabled,
          difficulty: emailPreferences.final_result.difficulty,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,type,difficulty'
        });

      if (finalResultError) throw finalResultError;

      // Save next_puzzle preference
      const { error: nextPuzzleError } = await supabase
        .from('email_preferences')
        .upsert({
          user_id: currentUser.id,
          type: 'next_puzzle',
          is_enabled: emailPreferences.next_puzzle.enabled,
          difficulty: 'all',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,type,difficulty'
        });

      if (nextPuzzleError) throw nextPuzzleError;

      // Save daily_reminder preference
      const { error: dailyReminderError } = await supabase
        .from('email_preferences')
        .upsert({
          user_id: currentUser.id,
          type: 'daily_reminder',
          is_enabled: emailPreferences.daily_reminder.enabled,
          difficulty: 'all',
          send_hour_utc: emailPreferences.daily_reminder.send_hour_utc,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,type,difficulty'
        });

      if (dailyReminderError) throw dailyReminderError;

      setPreferencesSaveMessage('Preferences saved successfully!');
      setTimeout(() => setPreferencesSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving email preferences:', error);
      setPreferencesSaveMessage('Failed to save preferences. Please try again.');
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === Infinity) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getUserStatus = () => {
    if (userProfile?.is_paid_founder) return 'Paid Founder';
    if (userProfile?.is_free_founder) return 'Founder';
    return 'Member';
  };

  const getStatusColor = () => {
    if (userProfile?.is_paid_founder) return '#B58B2E';
    if (userProfile?.is_free_founder) return '#2563EB';
    return '#6B7280';
  };

  const FounderBadge = ({ type }) => {
    const isFilled = type === 'paid';
    const color = type === 'paid' ? '#B58B2E' : '#2563EB';
    
    return (
      <svg width="16" height="18" viewBox="0 0 16 18" style={{ display: 'inline-block', marginLeft: '8px', verticalAlign: 'middle' }}>
        <path
          d="M8 0 L12 4 L16 4 L16 11 L8 18 L0 11 L0 4 L4 4 Z"
          fill={isFilled ? color : 'none'}
          stroke={color}
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  const handleLogout = async () => {
    const confirmed = window.confirm('Are you sure you want to log out?');
    if (!confirmed) return;

    await supabase.auth.signOut();
    navigateTo('landing');
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      alert('Please fill in both password fields');
      return;
    }

    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setIsChangingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        alert(`Failed to change password: ${error.message}`);
      } else {
        alert('Password changed successfully');
        setShowPasswordModal(false);
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account?\n\n' +
      'This will permanently remove:\n' +
      '• Your profile and username\n' +
      '• All your solve history\n' +
      '• Your Founder status (if applicable)\n\n' +
      'This action cannot be undone.'
    );

    if (!confirmed) return;

    const doubleCheck = window.confirm(
      'Final confirmation: Delete your account permanently?'
    );

    if (!doubleCheck) return;

    try {
      // Delete profile (cascades to solves due to foreign key)
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', currentUser.id);

      if (profileError) {
        console.error('Error deleting profile:', profileError);
        alert('Failed to delete account. Please contact support.');
        return;
      }

      // Sign out
      await supabase.auth.signOut();
      alert('Your account has been deleted.');
      navigateTo('landing');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('An error occurred. Please contact support.');
    }
  };

  // Redirect if not logged in
  if (!isLoggedIn) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 16px 0'
          }}>
            Account Access
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#6B7280',
            margin: '0 0 24px 0'
          }}>
            Please log in to view your account.
          </p>
          <button
            onClick={() => navigateTo('login')}
            style={{
              width: '100%',
              padding: '12px',
              background: '#FF8C00',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)'
            }}
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  // Wait for profile to load
  if (!userProfile) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          color: 'white',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      paddingBottom: '60px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Navigation */}
      {isMobile ? (
        <HamburgerMenu navigateTo={navigateTo} isLoggedIn={isLoggedIn} />
      ) : (
        <NavigationBar navigateTo={navigateTo} isLoggedIn={isLoggedIn} />
      )}

      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: isMobile ? '80px 20px 20px' : '120px 20px 20px'
      }}>
        {/* Page Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <h1 style={{
            fontSize: isMobile ? '32px' : '40px',
            fontWeight: '800',
            color: 'white',
            margin: '0 0 8px 0',
            letterSpacing: '-1px'
          }}>
            Account
          </h1>
          <p style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.8)',
            margin: 0
          }}>
            Manage your profile and preferences
          </p>
        </div>

        {/* User Identity Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontSize: isMobile ? '28px' : '36px',
              fontWeight: '800',
              color: '#1F2937',
              margin: 0,
              display: 'inline'
            }}>
              {userProfile.username}
            </h2>
            {userProfile.is_paid_founder && <FounderBadge type="paid" />}
            {userProfile.is_free_founder && !userProfile.is_paid_founder && <FounderBadge type="free" />}
          </div>

          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            background: '#F3F4F6',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            color: getStatusColor(),
            marginBottom: '12px'
          }}>
            {getUserStatus()}
          </div>

          <div style={{
            fontSize: '14px',
            color: '#6B7280',
            marginTop: '8px'
          }}>
            {currentUser.email}
          </div>

          {/* Founder Hub Access Button (Founders Only) */}
          {(userProfile.is_paid_founder || userProfile.is_free_founder) && (
            <button
              onClick={() => navigateTo('founderhub')}
              style={{
                marginTop: '20px',
                padding: '12px 24px',
                background: '#FF8C00',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 0, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 0, 0.4)';
              }}
            >
              Access Founder Hub
            </button>
          )}
        </div>

        {/* Player Snapshot */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 20px 0'
          }}>
            Player Snapshot
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#6B7280',
                marginBottom: '6px'
              }}>
                Puzzles Completed
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#1F2937'
              }}>
                {solveStats ? solveStats.totalCompleted : '0'}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#6B7280',
                marginBottom: '6px'
              }}>
                First Solve
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1F2937'
              }}>
                {solveStats ? solveStats.firstSolveDate : '—'}
              </div>
            </div>
          </div>

          <div style={{
            paddingTop: '20px',
            borderTop: '1px solid #E5E7EB'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#6B7280',
              marginBottom: '12px'
            }}>
              Best Times
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Easy</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                  {solveStats ? formatTime(solveStats.bestTimes.easy) : '—'}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Hard</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                  {solveStats ? formatTime(solveStats.bestTimes.hard) : '—'}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Master</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                  {solveStats ? formatTime(solveStats.bestTimes.master) : '—'}
                </div>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '20px',
            fontSize: '13px',
            color: '#9CA3AF',
            fontStyle: 'italic',
            textAlign: 'center'
          }}>
            More detailed stats coming soon
          </div>
        </div>

        {/* Email Notifications Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 8px 0'
          }}>
            Email Notifications
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: '0 0 24px 0',
            lineHeight: '1.5'
          }}>
            Choose when you'd like to receive email updates about puzzles
          </p>

          {isLoadingPreferences ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
              Loading preferences...
            </div>
          ) : (
            <>
              {/* Final Result Notification */}
              <div style={{
                padding: '16px 0',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1F2937',
                      marginBottom: '4px'
                    }}>
                      When final results are locked in
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#6B7280'
                    }}>
                      Get notified when today's rankings are final
                    </div>
                  </div>
                  <div
                    onClick={() => setEmailPreferences(prev => ({
                      ...prev,
                      final_result: { ...prev.final_result, enabled: !prev.final_result.enabled }
                    }))}
                    style={{
                      width: '50px',
                      height: '28px',
                      background: emailPreferences.final_result.enabled ? '#10B981' : '#D1D5DB',
                      borderRadius: '14px',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background 0.3s ease',
                      flexShrink: 0
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      background: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: emailPreferences.final_result.enabled ? '24px' : '2px',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                  </div>
                </div>

                {/* Difficulty Selector */}
                {emailPreferences.final_result.enabled && (
                  <div style={{
                    marginTop: '12px',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ fontSize: '13px', color: '#6B7280', width: '100%', marginBottom: '4px' }}>
                      Notify me for:
                    </div>
                    {['all', 'easy', 'hard', 'master'].map(diff => (
                      <button
                        key={diff}
                        onClick={() => setEmailPreferences(prev => ({
                          ...prev,
                          final_result: { ...prev.final_result, difficulty: diff }
                        }))}
                        style={{
                          padding: '6px 12px',
                          background: emailPreferences.final_result.difficulty === diff ? '#1e3a8a' : '#F3F4F6',
                          color: emailPreferences.final_result.difficulty === diff ? 'white' : '#4B5563',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          textTransform: 'capitalize'
                        }}
                      >
                        {diff === 'all' ? 'All Difficulties' : diff}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Next Puzzle Notification */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '4px'
                  }}>
                    When tomorrow's puzzle is live
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6B7280'
                  }}>
                    Get notified at midnight when new puzzles drop
                  </div>
                </div>
                <div
                  onClick={() => setEmailPreferences(prev => ({
                    ...prev,
                    next_puzzle: { enabled: !prev.next_puzzle.enabled }
                  }))}
                  style={{
                    width: '50px',
                    height: '28px',
                    background: emailPreferences.next_puzzle.enabled ? '#10B981' : '#D1D5DB',
                    borderRadius: '14px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease',
                    flexShrink: 0
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: emailPreferences.next_puzzle.enabled ? '24px' : '2px',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}></div>
                </div>
              </div>

              {/* Daily Reminder Notification */}
              <div style={{
                padding: '16px 0'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1F2937',
                      marginBottom: '4px'
                    }}>
                      Daily reminder
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#6B7280'
                    }}>
                      Receive a daily email reminder to solve
                    </div>
                  </div>
                  <div
                    onClick={() => setEmailPreferences(prev => ({
                      ...prev,
                      daily_reminder: { ...prev.daily_reminder, enabled: !prev.daily_reminder.enabled }
                    }))}
                    style={{
                      width: '50px',
                      height: '28px',
                      background: emailPreferences.daily_reminder.enabled ? '#10B981' : '#D1D5DB',
                      borderRadius: '14px',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background 0.3s ease',
                      flexShrink: 0
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      background: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: emailPreferences.daily_reminder.enabled ? '24px' : '2px',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                  </div>
                </div>

                {/* Time Picker */}
                {emailPreferences.daily_reminder.enabled && (
                  <div style={{
                    marginTop: '12px',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ fontSize: '13px', color: '#6B7280', width: '100%', marginBottom: '4px' }}>
                      Send at (UTC):
                    </div>
                    {[8, 12, 18].map(hour => (
                      <button
                        key={hour}
                        onClick={() => setEmailPreferences(prev => ({
                          ...prev,
                          daily_reminder: { ...prev.daily_reminder, send_hour_utc: hour }
                        }))}
                        style={{
                          padding: '6px 12px',
                          background: emailPreferences.daily_reminder.send_hour_utc === hour ? '#1e3a8a' : '#F3F4F6',
                          color: emailPreferences.daily_reminder.send_hour_utc === hour ? 'white' : '#4B5563',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {hour.toString().padStart(2, '0')}:00
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div style={{ marginTop: '24px' }}>
                <button
                  onClick={saveEmailPreferences}
                  disabled={isSavingPreferences}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: isSavingPreferences ? '#9CA3AF' : '#FF8C00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: isSavingPreferences ? 'not-allowed' : 'pointer',
                    boxShadow: isSavingPreferences ? 'none' : '0 4px 12px rgba(255, 140, 0, 0.4)'
                  }}
                >
                  {isSavingPreferences ? 'Saving...' : 'Save Preferences'}
                </button>

                {/* Save Message */}
                {preferencesSaveMessage && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    background: preferencesSaveMessage.includes('success') ? '#D1FAE5' : '#FEE2E2',
                    color: preferencesSaveMessage.includes('success') ? '#065F46' : '#991B1B',
                    borderRadius: '6px',
                    fontSize: '13px',
                    textAlign: 'center'
                  }}>
                    {preferencesSaveMessage}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Account Actions */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 20px 0'
          }}>
            Account Actions
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <button
              onClick={() => setShowPasswordModal(true)}
              style={{
                padding: '12px',
                background: '#F9FAFB',
                color: '#1F2937',
                border: '2px solid #E5E7EB',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              Change Password
            </button>

            <button
              onClick={handleLogout}
              style={{
                padding: '12px',
                background: '#F9FAFB',
                color: '#1F2937',
                border: '2px solid #E5E7EB',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              Log Out
            </button>

            <button
              onClick={() => setShowDeleteModal(true)}
              style={{
                padding: '12px',
                background: 'transparent',
                color: '#9CA3AF',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              Delete Account
            </button>
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }} onClick={() => setShowDeleteModal(false)}>
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: isMobile ? '32px 24px' : '40px 32px',
                  maxWidth: '400px',
                  width: '100%',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                }}
              >
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '800',
                  color: '#1F2937',
                  margin: '0 0 16px 0'
                }}>
                  Delete Account
                </h2>
                <p style={{
                  fontSize: '15px',
                  color: '#4B5563',
                  margin: '0 0 24px 0',
                  lineHeight: '1.6'
                }}>
                  This will permanently delete your account, profile, and all solve history. This action cannot be undone.
                </p>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    style={{
                      flex: '1',
                      padding: '12px',
                      background: '#F3F4F6',
                      color: '#4B5563',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    style={{
                      flex: '1',
                      padding: '12px',
                      background: '#DC2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }} onClick={() => setShowPasswordModal(false)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: isMobile ? '32px 24px' : '40px 32px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
              }}
            >
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#1F2937',
                margin: '0 0 24px 0'
              }}>
                Change Password
              </h2>

              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxSizing: 'border-box'
                }}
              />

              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '24px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxSizing: 'border-box'
                }}
              />

              <div style={{
                display: 'flex',
                gap: '12px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  style={{
                    flex: '1',
                    padding: '12px',
                    background: '#F3F4F6',
                    color: '#4B5563',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword}
                  style={{
                    flex: '1',
                    padding: '12px',
                    background: isChangingPassword ? '#9CA3AF' : '#FF8C00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: isChangingPassword ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer navigateTo={navigateTo} />
    </div>
  );
}

export default AccountPage;
